import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Check, Menu, Play, RotateCcw, BookOpen as BookIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { BIBLE_BOOKS } from '../data/bibleBooks';
import { getBookProgress, getAllBooksProgress, clearGameState } from '../utils/localStorage';
import { SideMenu } from './SideMenu';

export const BookSelectionPage = () => {
  const navigate = useNavigate();
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [allProgress, setAllProgress] = useState({});

  // Load all progress on mount
  useEffect(() => {
    setAllProgress(getAllBooksProgress());
  }, []);

  const handleBookSelect = (book) => {
    const progress = getBookProgress(book.id);
    setSelectedBook(book);

    // If book has progress, show modal
    if (progress.isStarted) {
      setShowProgressModal(true);
    }
  };

  const handleContinueGame = () => {
    if (selectedBook) {
      setShowProgressModal(false);
      navigate('/game', { state: { selectedBook, resumeGame: true } });
    }
  };

  const handleStartNewGame = () => {
    if (selectedBook) {
      // Clear previous game state
      clearGameState(selectedBook.id);
      setShowProgressModal(false);
      navigate('/game', { state: { selectedBook, resumeGame: false } });
    }
  };

  const handleStartGame = () => {
    if (selectedBook) {
      const progress = getBookProgress(selectedBook.id);

      // If book has progress, ask if they want to continue
      if (progress.isStarted) {
        setShowProgressModal(true);
      } else {
        navigate('/game', { state: { selectedBook, resumeGame: false } });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderBookCard = (book) => {
    const progress = allProgress[book.id] || { percentage: 0, isStarted: false };
    const isSelected = selectedBook?.id === book.id;
    const isCompleted = progress.percentage === 100;

    return (
      <Card
        key={book.id}
        onClick={() => handleBookSelect(book)}
        className={`p-4 cursor-pointer interactive border-2 transition-all ${isSelected
          ? 'border-primary bg-primary/5 shadow-lg'
          : isCompleted
            ? 'border-green-500/50 bg-green-500/5'
            : progress.isStarted
              ? 'border-yellow-500/30 bg-yellow-500/5'
              : 'border-transparent hover:border-primary/30'
          }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isSelected ? 'bg-primary' :
              isCompleted ? 'bg-green-500' :
                progress.isStarted ? 'bg-yellow-500' :
                  'bg-secondary'
              }`}>
              <BookOpen className={`w-6 h-6 ${isSelected || isCompleted || progress.isStarted ? 'text-white' : 'text-muted-foreground'
                }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{book.name}</h3>
                {isCompleted && (
                  <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                    Completado
                  </span>
                )}
                {progress.isStarted && !isCompleted && (
                  <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded-full">
                    En progreso
                  </span>
                )}
              </div>

              {/* Always show progress bar */}
              <div className="flex items-center gap-2 mt-2">
                <div className="h-2 flex-1 max-w-[200px] bg-secondary rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${isCompleted ? 'bg-green-500' :
                      progress.isStarted ? 'bg-yellow-500' :
                        'bg-primary'
                      }`}
                    style={{ width: `${progress.percentage || 0}%` }}
                  ></div>
                </div>
                <span className="text-sm text-muted-foreground min-w-[45px]">
                  {progress.percentage || 0}%
                </span>
              </div>

              {/* Show chapter info if started */}
              {progress.isStarted && (
                <p className="text-xs text-muted-foreground mt-1">
                  Capítulo {progress.currentChapter || 1} de {book.chapters} • {progress.totalVersesRevealed || 0} versículos revelados
                </p>
              )}
            </div>
          </div>
          {isSelected && (
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center ml-2">
              <Check className="w-5 h-5 text-primary-foreground" />
            </div>
          )}
          {!isSelected && (
            <span className="text-muted-foreground text-2xl ml-2">›</span>
          )}
        </div>
      </Card>
    );
  };

  const selectedProgress = selectedBook ? (allProgress[selectedBook.id] || {}) : {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 pb-24">
      {/* Side Menu */}
      <SideMenu open={menuOpen} onOpenChange={setMenuOpen} />

      {/* Progress Modal */}
      <Dialog open={showProgressModal} onOpenChange={setShowProgressModal}>
        <DialogContent className="sm:max-w-md border-0 bg-transparent p-0 shadow-none outline-none">
          <div className="bg-card border-2 border-primary/20 rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col max-h-[85vh]">
            <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-white text-center relative flex-shrink-0">
              <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <BookOpen className="w-32 h-32" />
              </div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/20 rounded-[1.5rem] flex items-center justify-center mx-auto mb-3 backdrop-blur-md border border-white/30 shadow-inner">
                  <Play className="w-8 h-8 text-white fill-current translate-x-0.5" />
                </div>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Juego en Curso</h2>
                <p className="opacity-90 font-medium text-sm">Tienes una partida guardada</p>
              </div>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar">
              <div className="bg-secondary/30 p-5 rounded-3xl relative overflow-hidden group border border-border/50">
                <div className="flex justify-between items-end mb-3">
                  <div>
                    <span className="text-[9px] font-black uppercase text-muted-foreground tracking-[0.2em] mb-1 block">Progreso del libro</span>
                    <h3 className="text-xl font-black text-foreground truncate max-w-[180px]">{selectedBook?.name}</h3>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-black text-primary">{selectedProgress.percentage || 0}%</span>
                  </div>
                </div>
                <div className="h-3 bg-secondary rounded-full overflow-hidden shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-400 via-primary to-primary rounded-full transition-all duration-1000 shadow-lg"
                    style={{ width: `${selectedProgress.percentage || 0}%` }}
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary/20 p-4 rounded-2xl border border-border/40 hover:bg-secondary/30 transition-colors">
                  <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mb-1">Capítulo</p>
                  <p className="text-xl font-bold">{selectedProgress.currentChapter || 1} <span className="text-[10px] text-muted-foreground font-medium">/ {selectedBook?.chapters}</span></p>
                </div>
                <div className="bg-secondary/20 p-4 rounded-2xl border border-border/40 hover:bg-secondary/30 transition-colors">
                  <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mb-1">Versículos</p>
                  <p className="text-xl font-bold">{selectedProgress.totalVersesRevealed || 0}</p>
                </div>
                <div className="bg-secondary/20 p-4 rounded-2xl border border-border/40 hover:bg-secondary/30 transition-colors">
                  <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mb-1">Puntos</p>
                  <p className="text-xl font-bold">{selectedProgress.score || 0}</p>
                </div>
                <div className="bg-secondary/20 p-4 rounded-2xl border border-border/40 hover:bg-secondary/30 transition-colors">
                  <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mb-1">Dificultad</p>
                  <p className="text-xl font-bold">Nivel {selectedProgress.level || 1}</p>
                </div>
              </div>

              {selectedProgress.lastPlayed && (
                <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-muted-foreground uppercase tracking-[0.15em] py-1">
                  <span className="w-1 h-1 bg-yellow-500 rounded-full animate-pulse"></span>
                  Última sesión: {formatDate(selectedProgress.lastPlayed)}
                </div>
              )}

              <div className="space-y-3 pt-1">
                <Button
                  onClick={handleContinueGame}
                  className="w-full h-14 text-lg font-black bg-primary hover:bg-primary/90 text-primary-foreground rounded-[1.2rem] shadow-xl shadow-primary/20 group transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Play className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform fill-current" />
                  RETOMAR PARTIDA
                </Button>
                <Button
                  onClick={handleStartNewGame}
                  variant="ghost"
                  className="w-full h-12 text-xs font-bold text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-xl transition-all"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-2" />
                  Empezar desde el principio
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="sticky top-0 bg-background/80 glass border-b border-border backdrop-blur-lg z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-secondary rounded-full interactive"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold flex-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Selecciona un Libro
          </h1>
          <button
            onClick={() => setMenuOpen(true)}
            className="p-2 hover:bg-secondary rounded-full interactive"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-6">
        {/* Search Bar */}
        <div className="mb-6 relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar libro (ej. Génesis)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 h-14 text-base bg-card border-2 rounded-2xl"
          />
        </div>

        {/* Old Testament */}
        <section className="mb-8">
          <h2 className="text-sm font-bold text-muted-foreground uppercase tracking-wide mb-4 px-2">
            Antiguo Testamento ({BIBLE_BOOKS.OLD_TESTAMENT.length} libros)
          </h2>
          <div className="space-y-3">
            {BIBLE_BOOKS.OLD_TESTAMENT.filter(book =>
              book.name.toLowerCase().includes(searchTerm.toLowerCase())
            ).map(renderBookCard)}
          </div>
        </section>

        {/* New Testament */}
        <section className="mb-8">
          <h2 className="text-sm font-bold text-muted-foreground uppercase tracking-wide mb-4 px-2">
            Nuevo Testamento ({BIBLE_BOOKS.NEW_TESTAMENT.length} libros)
          </h2>
          <div className="space-y-3">
            {BIBLE_BOOKS.NEW_TESTAMENT.filter(book =>
              book.name.toLowerCase().includes(searchTerm.toLowerCase())
            ).map(renderBookCard)}
          </div>
        </section>
      </main>

      {/* Fixed Bottom Button */}
      {selectedBook && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent">
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={handleStartGame}
              size="lg"
              className="w-full h-16 text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-xl"
            >
              <span className="mr-3 text-xl">▶</span>
              {selectedProgress.isStarted ? 'Continuar Juego' : 'Comenzar Juego'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const BookOpen = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);
